import { useQuery } from '@tanstack/react-query'
import { useLocation, useRouter } from '@tanstack/react-router'
import type { MouseEvent } from 'react'
import type { ExamsApi } from './api/exams'
import { codeOf, slugOf } from './chosenExam'
import { Menu, MenuRow, menuItem } from './Menu'

/**
 * Which Exam the Admin Site is about, and the way to make it another one.
 *
 * Beside the mark, top-left, which is where Webflow, Whop, Postman and Coda put the
 * workspace switcher: the corner a person looks at to see whose work they are in. An Admin
 * manages one Exam at a time, so the Exam is chosen here once and every area is about it.
 *
 * Choosing keeps the screen and swaps the Exam -- `/tmua/sittings` becomes `/esat/sittings`
 * -- so switching mid-task lands on the same task for the other Exam. The address is what
 * carries the choice (app:R84); this only offers the others.
 *
 * Plain anchors rather than the router's Link: the address is built from the one on screen
 * rather than named as a route, and Link will not take an address it cannot type. The
 * router's history is pushed instead, which is the same navigation without the reload; a
 * modified click is left to the browser, so a new tab still works.
 */
export function ExamSwitcher({ exams, exam }: { exams: ExamsApi; exam: string | null }) {
  const catalogue = useQuery({ queryKey: ['exams'], queryFn: () => exams.list() })
  const { pathname } = useLocation()
  const router = useRouter()

  const follow = (href: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    router.history.push(href)
  }

  return (
    // No visible caption over the code: a masthead that already reads "EF Admin | TMUA" does not
    // need a word telling an Admin that TMUA is an Exam. The group keeps the name for a screen
    // reader, which has no masthead to look at and would otherwise meet a button called TMUA.
    <div role="group" aria-label="Exam">
      <Menu label={exam ? codeOf(exam) : 'Choose an Exam'} text align="left">
        {(catalogue.data ?? []).map((entry) => {
          const slug = slugOf(entry.code)
          const href = switched(pathname, exam, slug)

          return (
            <MenuRow key={entry.code}>
              <a
                href={href}
                onClick={follow(href)}
                aria-current={slug === exam ? 'true' : undefined}
                className={`${menuItem} flex items-baseline gap-3 whitespace-nowrap`}
              >
                {/* A tab stop, so every name starts at the same x rather than wherever its own
                    code happened to end. 5.5ch clears the longest code this domain has --
                    ENGAA, five bold characters; a longer one would push its own name right and
                    nothing else, and the fix then is a subgrid across the panel's rows. */}
                <span className="min-w-[5.5ch] font-bold">{entry.code}</span>
                <span className="text-muted">{entry.name}</span>
              </a>
            </MenuRow>
          )
        })}
        {catalogue.isPending && (
          <MenuRow>
            <p className="px-3 py-2 text-muted">Reading the Exams...</p>
          </MenuRow>
        )}
        {catalogue.isError && (
          <MenuRow>
            <p role="alert" className="px-3 py-2 text-red-700">
              {catalogue.error.message}
            </p>
          </MenuRow>
        )}
      </Menu>
    </div>
  )
}

/**
 * The same screen for another Exam: `/tmua/sittings` for ESAT is `/esat/sittings`. Off
 * an Exam's screens -- the chooser, the cog's pages -- it is that Exam's own address.
 */
export function switched(pathname: string, from: string | null, to: string): string {
  const prefix = `/${from}`

  return from !== null && (pathname === prefix || pathname.startsWith(`${prefix}/`))
    ? `/${to}${pathname.slice(prefix.length)}`
    : `/${to}`
}
